import type { CasinoSettings } from '../../types';

export const ImageTransform = {
  FEATURED_GAME: 'featured-game',
  HIGH: 'high',
  LOW: 'low',
  MID: 'mid',
} as const;

export type ImageTransform = (typeof ImageTransform)[keyof typeof ImageTransform];

// --------------------

export type ActionType = 'launchGame' | 'openUrl' | 'navigateTo';

export type CtaAction = {
  actionType: ActionType;
  gameId?: string;
  path?: string;
  url?: string;
};

export type BannerSegmentationType = 'LOGGED_IN' | 'LOGGED_OUT';
export type BannerDeviceType = 'DESKTOP' | 'IOS' | 'ANDROID';
export type BannerContentAlignment = 'left' | 'right' | 'center';

export type Banner = {
  action?: CtaAction;
  campaign_ids: string[] | null;
  cta_label: string;
  desktop_image_type: string;
  desktop_image: string;
  desktop_vid_h265?: string;
  desktop_vid: string;
  device_list: string;
  duration: number;
  end_date: string;
  is_gamification_banner?: boolean;
  is_seo_banner?: boolean;
  is_welcome_banner?: boolean;
  mobile_image_small_type: string;
  mobile_image_small: string;
  mobile_image_type: string;
  mobile_image: string;
  mobile_vid_h265?: string;
  mobile_vid_small_h265?: string;
  mobile_vid_small?: string;
  mobile_vid: string;
  order: number;
  segmentation_list: string;
  small_image_type?: string; // can be missing in some cases
  small_image?: string; // can be missing in some cases
  start_date: string;
  subaction_description: string;
  subaction?: CtaAction;
  subtitle: string;
  title: string;
};

export type StoryAPI = {
  id: string;
  card_image: string;
  is_sticky?: boolean;
  start_date: string;
  end_date: string;
  campaign_ids: string | null;
  order: number;
  translations: {
    title: string;
  }[];
  inner_story_items: {
    tc_stories_id: string;
    detail_asset: string;
    detail_video_poster: string | null;
    cta_action: CtaAction;
    order: number;
    translations: {
      cta_label: string;
    }[];
  }[];
};

export type Story = {
  id: string;
  uniqueId: string; // id+startDate to make sure it is not marked as viewed if reused in BO
  title: string;
  previewImage: string;
  isPinned?: boolean;
  isViewed: boolean;
  campaignIds: string[] | null;
  order: number;
  innerStoryItems: InnerStoryItem[];
};

export type InnerStoryItem = {
  storyAsset: string;
  storyDetailVideoPoster: string | null;
  ctaLabel: string;
  ctaAction: CtaAction;
  order: number;
};

export type HeroBanner = Array<Banner>;

// --------------------

export type Link = {
  title: string;
  nav: string;
  followEnabled?: boolean;
  sameSite?: boolean;
};

export type Icon = {
  icon: string;
  nav?: string;
};

export type Footer = {
  links: Link[];
  paymentOptions: string[];
  responsibleGaming: {
    text: string;
    link: string;
    icons: Icon[];
  };
  // WIP: To be changed with proper BE in place
  helpCenterText: string;
};

export type TokenRenewOptions = {
  renewGracePeriodM: number;
  retryIfFailedSec: number;
  tokenTtlM: number;
};

export type LoginPromotionalModalConfig = {
  enabled: boolean;
  startDatetime: string; // ISO 8601
  endDatetime: string; // ISO 8601
  title: string;
  ctaLabel: string;
  ctaUrl?: string;
  imageUrl: string;
  videoUrlH265?: string;
  videoUrlH264?: string;
  hasAudio?: boolean;
  showOnce?: boolean;
  modalId?: string;
};

export type WelcomeModalConfig = {
  enabled: boolean;
  startDatetime: string; // ISO 8601 naive (Europe/Berlin)
  endDatetime: string; // ISO 8601 naive (Europe/Berlin)
  title: string;
  ctaLabel: string;
  ctaUrl?: string;
  imageUrl: string;
  videoUrlH265?: string;
  videoUrlH264?: string;
  hasAudio?: boolean;
};

export type WelcomeOfferConfig = {
  startDatetime: string; // ISO 8601 naive (Europe/Berlin)
  endDatetime: string; // ISO 8601 naive (Europe/Berlin)
  desktop: { title: string; subtitle: string };
  mobile: { title: string; subtitle: string };
};

export type CasinoConfig = {
  appStores: { android: string; ios: string };
  appVersionsForce: { android: string; ios: string };
  automatedLimitConfirmationPopup?: boolean;
  beliebteSpieleSlug?: string;
  cassieLoaderEnabled?: boolean;
  cassieWidgetProfileId?: number;
  /**
   * BackOffice switch between the Cornucopia cashier and the legacy eBET flow
   * (IGM-4161). Absent or `false` means eBET. Dev-panel session override in
   * `util/overrides.ts`.
   */
  cornucopiaEnabled?: boolean;
  feedDisconnectTimeout?: number;
  footer: Footer;
  freespinsLogoutModalEnabled?: boolean;
  forceMaintenanceForLegacyIosApp?: boolean;
  generalFeedEnabled?: boolean;
  iOS26FixEnabled?: boolean;
  gamificationDisabled?: boolean;
  gamificationMaintenanceEnabled?: boolean;
  hapticFeedbackEnabled?: boolean;
  loginPromotionalModal?: LoginPromotionalModalConfig;
  welcomeModal?: WelcomeModalConfig;
  welcomeOffer?: WelcomeOfferConfig;
  maintenanceIgnoreOrigins: string;
  migrateIdOnGameLaunchEnabled?: boolean;
  nativeNotificationsEnabled?: boolean;
  paymentTeaserOptions?: string[];
  offlineModalEnabled?: boolean;
  onboardingEnabled?: boolean;
  /**
   * BackOffice payment maintenance switch (IGM-4160). When `enabled` is `true`,
   * starting any deposit or withdrawal shows a blocking maintenance modal
   * instead — on both the Cornucopia cashier and the legacy eBET flow. `title`,
   * `copy`, and `cta` are optional modal copy; each missing or blank field falls
   * back to its i18n default and never affects activation (see
   * `payment-maintenance.ts`). Dev-panel session override in `util/overrides.ts`.
   */
  paymentMaintenance?: {
    enabled: boolean;
    title?: string;
    copy?: string;
    cta?: string;
  };
  themingEnabled?: boolean;
  rateTheApp: {
    enabled?: boolean;
    reminderInMin?: number;
    sessionReminderInMin?: number;
    minWinnings?: number;
  };
  regLobbyId?: string;
  settings: CasinoSettings;
  smartBannerEnabled?: boolean;
  stateDisclaimerModalEnabled?: boolean;
  tokenRenew: TokenRenewOptions;
  useIndependentLugasCdbLimits?: boolean;
  lowBalanceFlyer?: {
    enabled: boolean;
    thresholdCents: number;
  };
  vaixTrackingEnabled?: boolean;
  bestMatch?: {
    categories: { label: string; description: string; icon: string; tags: string[] }[];
    mechanics: { label: string; description: string; icon: string; tags: string[] }[];
  };
};

// --------------------

export type FeaturedGame = {
  desktop_background_content_type: string;
  desktop_background: string;
  desktop_game_image_content_type: string;
  desktop_game_image: string;
  desktop_text: string;
  end_date: string;
  game_id: string;
  mobile_background_content_type: string;
  mobile_background: string;
  mobile_game_image_content_type: string;
  mobile_game_image: string;
  mobile_text: string;
  name: string;
  offer: string;
  start_date: string;
  title: string;
  translations: {
    mobile_text: string;
    desktop_text: string;
    offer?: string;
  }[];
};

// --------------------

export type ClubThousand = {
  card_description: string;
  card_image: string;
  detail_description: string;
  detail_image: string;
  game_id?: string;
  order: number;
  show_on_homepage?: boolean /* defaults to `true` from BE */;
  subtitle: string;
  title: string;
  translations: {
    title: string;
    subtitle: string;
    card_description: string;
    detail_description: string;
  }[];
};

// --------------------

export type SEOHtmlContent = {
  path: string;
  content: SEOHtmlContentTags;
};

// --------------------

export type FAQ = {
  id: string;
  title: string;
  content: string;
};

export type SeoFAQ = {
  q: string;
  a: string;
};

export type SEOHtmlContentTags = {
  metaTitle?: string;
  metaDescription?: string;
  footerText?: string;
  faq?: SeoFAQ[];
};

// --------------------

export type StaticPageAPI = {
  item_type: string;
  path: string;
  should_be_added_to_sitemap: boolean | null;
  should_be_added_to_sitemap_xml: boolean | null;
  image_list: string[] | null;
  order: number;
  translations: {
    content: string | null;
    meta_title: string | null;
    meta_description: string | null;
    footer_label: string | null;
    faq: SeoFAQ[] | null;
  }[];
};

export type StaticPageSeo = {
  metaTitle: string | null;
  metaDescription: string | null;
  faq: SeoFAQ[] | null;
};

export type StaticPageItemType = 'static-page' | 'footer-link';

export type StaticPage = {
  itemType: StaticPageItemType;
  path: string;
  content: string | null;
  images: string[] | null;
  footerLabel: string | null;
  shouldBeAddedToSitemap: boolean;
  shouldBeAddedToSitemapXml: boolean;
  seo: StaticPageSeo;
  order: number;
};

// --------------------

export type CampaignNameMapping = {
  campaignId: string;
  title: string;
};

// --------------------

export type BECampaignInfoCMS = {
  id: string;
  image_thumbnail: string | null;
  image_banner: string | null;
  post_optIn_cta_link: string;
  translations: [
    {
      title: string;
      info: string | null;
      tac: string | null;
      subtitle: string | null;
      post_optIn_text: string | null;
      post_optIn_button_label: string | null;
      ibc_title: string | null;
    },
  ];
};

export type CMSPromotionTranslations = {
  title: string;
  subtitle: string;
  description: string;
  ctaTitle: string;
};

export type CMSPromotion = {
  ctaUrl: string | null;
  date_created: string;
  start_date: string | null;
  end_date: string | null;
  id: string;
  image_banner: string;
  image_thumbnail: string;
  order: number;
  translations: CMSPromotionTranslations[];
  is_gamification_promotion?: boolean;
};
